import { Injectable, PLATFORM_ID, PendingTasks, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  Observable,
  defer,
  finalize,
  firstValueFrom,
  from,
  shareReplay,
  tap,
  throwError,
  timeout,
} from 'rxjs';
import {
  SSR_CLIENT_TOKEN_FAILURE_BACKOFF_MS,
  SSR_CLIENT_TOKEN_RENEW_MARGIN_MS,
  SSR_CLIENT_TOKEN_TIMEOUT_MS,
  ssrClientTokenStore,
} from './ssr-client-token.store';

/**
 * Forma del response de `/oauth/token` (Laravel Passport) que reutilizan
 * todos los grant types. `refresh_token` sólo viene con grant=password.
 */
export interface TokenResponse {
  token_type: string;
  expires_in: number;
  access_token: string;
  refresh_token?: string;
}

// Keys de localStorage. Prefijo `perron_` para evitar colisiones con otras
// apps en el mismo dominio (p.ej. al probar con `localhost`).
const USER_ACCESS_KEY = 'perron_access_token';
const USER_REFRESH_KEY = 'perron_refresh_token';
const CLIENT_ACCESS_KEY = 'perron_client_token';
const CLIENT_EXPIRES_KEY = 'perron_client_token_expires';
const CART_TOKEN_KEY = 'perron_cart_token';

/**
 * Gestor de persistencia de tokens. En browser usa `localStorage`; en SSR,
 * el *client token* vive en un almacén **de proceso** (`ssr-client-token.store.ts`)
 * compartido por todas las requests, para que los fetches server-side puedan
 * autenticar sin negociar un token por página.
 *
 * Maneja tres tipos de token, independientes entre sí:
 *  - **User tokens** (access + refresh): sesión del cliente logueado
 *    (browser-only).
 *  - **Client token** (+ expiresAt): token de aplicación (OAuth
 *    client_credentials). Browser: localStorage. Server: almacén de proceso.
 *    Compartirlo entre requests es seguro porque no identifica a ningún
 *    usuario ni lleva datos de sesión: vale igual para cualquier visitante.
 *  - **Cart token**: identificador del carrito sin usuario (browser-only).
 *
 * Los user tokens y el cart token pertenecen a un visitante concreto: jamás
 * se guardan en servidor, y menos a nivel de proceso, donde se filtrarían a
 * las requests de otros visitantes.
 */
@Injectable({ providedIn: 'root' })
export class TokenService {
  private readonly platformId = inject(PLATFORM_ID);
  // Una instancia por app (por request en SSR): marca la app como no estable
  // mientras espera una negociación compartida que corre en la zona de otra.
  private readonly pendingTasks = inject(PendingTasks);

  /** True solo cuando corremos en browser (no en SSR). */
  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  // ── User tokens (password grant) ──────────────────────────────────

  /** Access token del usuario logueado, o `null` si no hay sesión. */
  getUserToken(): string | null {
    return this.isBrowser ? localStorage.getItem(USER_ACCESS_KEY) : null;
  }

  /** Refresh token del usuario (para renovar su access_token cuando expira). */
  getUserRefreshToken(): string | null {
    return this.isBrowser ? localStorage.getItem(USER_REFRESH_KEY) : null;
  }

  /** Guarda el par access+refresh tras un login exitoso. */
  setUserTokens(accessToken: string, refreshToken: string): void {
    if (!this.isBrowser) return;
    localStorage.setItem(USER_ACCESS_KEY, accessToken);
    localStorage.setItem(USER_REFRESH_KEY, refreshToken);
  }

  /** Borra los tokens de usuario (logout, 401 irrecuperable). */
  clearUserTokens(): void {
    if (!this.isBrowser) return;
    localStorage.removeItem(USER_ACCESS_KEY);
    localStorage.removeItem(USER_REFRESH_KEY);
  }

  // ── Client token (client_credentials) ─────────────────────────────

  /** Token de aplicación actual (usado por el interceptor para endpoints públicos). */
  getClientToken(): string | null {
    if (!this.isBrowser) return ssrClientTokenStore.token;
    return localStorage.getItem(CLIENT_ACCESS_KEY);
  }

  /**
   * Guarda el token de aplicación + su instante de expiración calculado
   * (`Date.now() + expires_in*1000`). El expiresAt sirve para decidir si
   * podemos reutilizarlo sin pegarle al backend.
   */
  setClientToken(response: TokenResponse): void {
    const expiresAt = Date.now() + response.expires_in * 1000;
    if (!this.isBrowser) {
      ssrClientTokenStore.token = response.access_token;
      ssrClientTokenStore.expiresAt = expiresAt;
      return;
    }
    localStorage.setItem(CLIENT_ACCESS_KEY, response.access_token);
    localStorage.setItem(CLIENT_EXPIRES_KEY, expiresAt.toString());
  }

  /**
   * Borra el token cliente. Lo invoca el `authInterceptor` cuando recibe
   * un 401 para forzar renegociación en la siguiente request.
   *
   * En servidor, con `staleToken` solo borra si el token guardado sigue siendo
   * el que falló: un 401 que llega tarde con el token viejo no debe tirar el
   * que otra request ya renovó para todo el proceso. La negociación en vuelo
   * no se toca: es justo la que queremos. En browser el argumento se ignora.
   */
  clearClientToken(staleToken?: string): void {
    if (!this.isBrowser) {
      if (staleToken !== undefined && ssrClientTokenStore.token !== staleToken) return;
      ssrClientTokenStore.token = null;
      ssrClientTokenStore.expiresAt = 0;
      return;
    }
    localStorage.removeItem(CLIENT_ACCESS_KEY);
    localStorage.removeItem(CLIENT_EXPIRES_KEY);
  }

  /**
   * True si hay token cliente guardado y su `expiresAt` local todavía no
   * venció. No valida contra el backend — si el server revoca el token
   * este método puede devolver true igualmente (para eso el interceptor
   * hace auto-retry en 401). En servidor lo da por caducado
   * `SSR_CLIENT_TOKEN_RENEW_MARGIN_MS` antes, para que ninguna request lo use
   * en su último segundo.
   */
  hasValidClientToken(): boolean {
    if (!this.isBrowser) {
      return (
        !!ssrClientTokenStore.token &&
        Date.now() < ssrClientTokenStore.expiresAt - SSR_CLIENT_TOKEN_RENEW_MARGIN_MS
      );
    }
    const token = localStorage.getItem(CLIENT_ACCESS_KEY);
    const expiresAt = localStorage.getItem(CLIENT_EXPIRES_KEY);
    if (!token || !expiresAt) return false;
    return Date.now() < parseInt(expiresAt, 10);
  }

  /**
   * Solo servidor. Funde en una sola negociación las peticiones concurrentes
   * de token de todo el proceso: la primera lanza `source$` y las demás esperan
   * su resultado. Tras un fallo, durante `SSR_CLIENT_TOKEN_FAILURE_BACKOFF_MS`
   * devuelve error sin lanzar nada, para no martillear a la API. Una
   * negociación que supera `SSR_CLIENT_TOKEN_TIMEOUT_MS` se aborta y cuenta
   * como fallo: todas las requests del proceso la esperan, no pueden colgarse.
   *
   * Cada llamante espera vía Promise (vuelve a su propia zona) y registra una
   * tarea pendiente en su app: sin ella, una app cuyo único trabajo es esperar
   * el POST de otra podría darse por estable y serializar el HTML antes de tiempo.
   */
  shareServerClientTokenRequest(source$: Observable<TokenResponse>): Observable<TokenResponse> {
    if (!ssrClientTokenStore.inFlight) {
      if (Date.now() - ssrClientTokenStore.lastFailureAt < SSR_CLIENT_TOKEN_FAILURE_BACKOFF_MS) {
        return throwError(() => new Error('[auth] client token en backoff tras un fallo'));
      }
      ssrClientTokenStore.inFlight = source$.pipe(
        // Antes del `tap`: el vencimiento cuenta como fallo y activa el backoff.
        timeout({ first: SSR_CLIENT_TOKEN_TIMEOUT_MS }),
        tap({
          next: () => {
            ssrClientTokenStore.lastFailureAt = 0;
            // Una línea por negociación y proceso, para cruzarla con el access log
            // de la API. Nunca el token ni nada de la request.
            const pid = (globalThis as { process?: { pid?: number } }).process?.pid ?? '?';
            console.info(`[auth] client token negociado · pid ${pid}`);
          },
          error: () => {
            ssrClientTokenStore.lastFailureAt = Date.now();
          },
        }),
        finalize(() => {
          ssrClientTokenStore.inFlight = null;
        }),
        shareReplay(1),
      );
    }
    const shared$ = ssrClientTokenStore.inFlight;
    return defer(() => {
      const done = this.pendingTasks.add();
      return from(firstValueFrom(shared$)).pipe(finalize(done));
    });
  }

  // ── Cart token (carrito anónimo) ──────────────────────────────────

  /** Token del carrito actual (persistente aunque el usuario cierre pestaña). */
  getCartToken(): string | null {
    return this.isBrowser ? localStorage.getItem(CART_TOKEN_KEY) : null;
  }

  /** Guarda el token del carrito tras crearlo o cambiarlo. */
  setCartToken(token: string): void {
    if (!this.isBrowser) return;
    localStorage.setItem(CART_TOKEN_KEY, token);
  }

  /** Borra el token del carrito (checkout completado, carrito abandonado, etc.). */
  clearCartToken(): void {
    if (!this.isBrowser) return;
    localStorage.removeItem(CART_TOKEN_KEY);
  }
}
